/**
 * PeticionRapidaDialog — modal de UNA pantalla para crear una petición + línea + 2
 * componentes (Montaje y Producción) de un solo POST.
 *
 * Pensado para agencias (DIG/IP/VF/PP) donde el flujo "borrador → presupuesto →
 * aceptación → conversión a petición" es demasiado lento. Aquí en 10-30 segundos
 * tienes el trabajo listo para montadores.
 *
 * Atajos:
 *  - Cmd/Ctrl + Enter → guardar
 *  - Tab navegación natural en grid
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import ClienteCombobox from "@/components/ClienteCombobox";
import EspacioCombobox from "@/components/EspacioCombobox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Zap, Loader2, Upload, X as XIcon, File as FileIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EMPTY_FORM = {
  cliente_id: "",
  marca: "",
  fecha_montaje: "",
  descripcion: "",
  centro_texto: "",
  espacio_id: "",
  referencia_cliente: "",
  observaciones: "",
  estado: "aprobada",
  cantidad: 1,
  metros_cuadrados: "",
  altura_cm: "",
  importe_montaje: "",
  importe_produccion: "",
  quien_produce: "DNF",
};

export default function PeticionRapidaDialog({ clientes = [], onCreated, triggerClassName }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [espacios, setEspacios] = useState([]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Cargar espacios del cliente seleccionado
  useEffect(() => {
    if (!open || !form.cliente_id) { setEspacios([]); return; }
    api.get("/espacios", { params: { cliente_id: form.cliente_id, limit: 500 } })
      .then((r) => setEspacios(r.data || []))
      .catch(() => setEspacios([]));
  }, [open, form.cliente_id]);

  const submit = useCallback(async (cerrar = true) => {
    if (!form.cliente_id) { toast.error("Selecciona un cliente"); return; }
    if (!form.descripcion?.trim() && !form.centro_texto?.trim()) {
      toast.error("Añade al menos una descripción del trabajo");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        cliente_id: form.cliente_id,
        marca: form.marca || null,
        fecha_montaje: form.fecha_montaje || null,
        descripcion: form.descripcion || null,
        centro_texto: form.centro_texto || null,
        espacio_id: form.espacio_id || null,
        referencia_cliente: form.referencia_cliente || null,
        observaciones: form.observaciones || null,
        estado: form.estado || "aprobada",
        cantidad: parseFloat(form.cantidad) || 1,
        metros_cuadrados: form.metros_cuadrados ? parseFloat(form.metros_cuadrados) : null,
        altura_cm: form.altura_cm ? parseFloat(form.altura_cm) : null,
        importe_montaje: parseFloat(form.importe_montaje) || 0,
        importe_produccion: parseFloat(form.importe_produccion) || 0,
        quien_produce: form.quien_produce || "DNF",
      };
      const { data } = await api.post("/peticiones/rapida", payload);
      // Subir adjuntos (Drag & Drop) si los hay
      if (droppedFiles.length > 0) {
        setUploadingFiles(true);
        try {
          for (const f of droppedFiles) {
            const fd = new FormData();
            fd.append("file", f);
            await api.post(`/peticiones/${data.peticion_id}/adjunto`, fd,
              { headers: { "Content-Type": "multipart/form-data" } });
          }
          toast.success(`📎 ${droppedFiles.length} adjunto(s) subidos`);
        } catch (upErr) {
          toast.error("La petición se creó pero algún adjunto falló al subir");
        } finally { setUploadingFiles(false); }
      }
      toast.success(`✅ ${data.codigo} creada · ${data.componentes_creados} componente(s)`, {
        action: { label: "Abrir", onClick: () => nav(`/peticiones/${data.peticion_id}`) },
        duration: 5000,
      });
      if (onCreated) onCreated(data);
      if (cerrar) {
        setOpen(false);
        setForm(EMPTY_FORM);
        setDroppedFiles([]);
      } else {
        setForm((f) => ({ ...EMPTY_FORM, cliente_id: f.cliente_id, fecha_montaje: f.fecha_montaje, marca: f.marca }));
        setDroppedFiles([]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando petición rápida");
    } finally { setSaving(false); }
  }, [form, nav, onCreated, droppedFiles]);


  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="btn-peticion-rapida"
          className={triggerClassName || "bg-fuchsia-500 hover:bg-fuchsia-400 text-zinc-950 font-semibold"}
        >
          <Zap className="w-4 h-4 mr-2" /> Petición rápida
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl"
        data-testid="dialog-peticion-rapida"
        onKeyDown={onKeyDown}
      >
        <DialogHeader>
          <DialogTitle className="text-fuchsia-300 flex items-center gap-2">
            <Zap className="w-5 h-5" /> Petición rápida
          </DialogTitle>
          <div className="text-[11px] text-zinc-500">
            Una sola pantalla — pensado para agencias (DIG, IP, VF, PP). Crea petición + línea + componentes en un click. Atajo: <kbd className="px-1.5 py-0.5 bg-zinc-900 border border-zinc-700 rounded text-[10px]">Ctrl/Cmd + Enter</kbd>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto pr-1">
          {/* === Fila 1: Quién y qué === */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-5">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Cliente *</Label>
              <ClienteCombobox
                value={form.cliente_id}
                onChange={(id) => setField("cliente_id", id)}
                clientes={clientes}
                testId="rapida-cliente"
              />
            </div>
            <div className="col-span-4">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Marca / Producto</Label>
              <Input
                value={form.marca}
                onChange={(e) => setField("marca", e.target.value)}
                placeholder="YSL, Kerastase, Polaroid…"
                className="bg-zinc-900 border-zinc-800"
                data-testid="rapida-marca"
              />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Fecha montaje</Label>
              <Input
                type="date"
                value={form.fecha_montaje}
                onChange={(e) => setField("fecha_montaje", e.target.value)}
                className="bg-zinc-900 border-zinc-800"
                data-testid="rapida-fecha"
              />
            </div>
          </div>

          {/* === Fila 2: Dónde === */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-6">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Espacio (si está en BD)</Label>
              <EspacioCombobox
                value={form.espacio_id}
                onChange={(id) => setField("espacio_id", id || "")}
                espacios={espacios}
                clienteId={form.cliente_id || null}
                disabled={!form.cliente_id}
                placeholder={form.cliente_id ? "Buscar espacio…" : "Selecciona cliente primero"}
                allowNone
                noneLabel="— Sin espacio (uso el campo libre)"
                emptyText={form.cliente_id ? "Sin espacios para este cliente. Usa el campo Centro/Tienda." : "Selecciona cliente primero"}
                testId="rapida-espacio"
              />
            </div>
            <div className="col-span-6">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Centro / Tienda (texto libre)</Label>
              <Input
                value={form.centro_texto}
                onChange={(e) => setField("centro_texto", e.target.value)}
                placeholder="Aromas O'Donnell, ECI Duque, Orange Marchena…"
                className="bg-zinc-900 border-zinc-800"
                data-testid="rapida-centro"
              />
            </div>
          </div>

          {/* === Fila 3: Trabajo === */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Material / Montaje *</Label>
              <Textarea
                value={form.descripcion}
                onChange={(e) => setField("descripcion", e.target.value)}
                rows={2}
                placeholder="Vinilo caja de luz 5530, Montaje 3 lonas, etc."
                className="bg-zinc-900 border-zinc-800 resize-none"
                data-testid="rapida-descripcion"
              />
            </div>
          </div>

          {/* === Fila 4: Métricas === */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Cantidad</Label>
              <Input type="number" step="0.5" min="0" value={form.cantidad}
                onChange={(e) => setField("cantidad", e.target.value)}
                className="bg-zinc-900 border-zinc-800" data-testid="rapida-cantidad" />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">m²</Label>
              <Input type="number" step="0.1" min="0" value={form.metros_cuadrados}
                onChange={(e) => setField("metros_cuadrados", e.target.value)}
                placeholder="4.8"
                className="bg-zinc-900 border-zinc-800" data-testid="rapida-m2" />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Altura (cm)</Label>
              <Input type="number" step="1" min="0" value={form.altura_cm}
                onChange={(e) => setField("altura_cm", e.target.value)}
                placeholder="200"
                className="bg-zinc-900 border-zinc-800" data-testid="rapida-altura" />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Estado inicial</Label>
              <Select value={form.estado} onValueChange={(v) => setField("estado", v)}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="rapida-estado">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="borrador">Borrador</SelectItem>
                  <SelectItem value="aprobada">Aprobada (lista para montar)</SelectItem>
                  <SelectItem value="en_diseno">En diseño</SelectItem>
                  <SelectItem value="en_produccion">En producción</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* === Fila 5: Económico === */}
          <div className="grid grid-cols-12 gap-3 bg-fuchsia-500/5 border border-fuchsia-500/20 rounded-lg p-3">
            <div className="col-span-12 text-[10px] text-fuchsia-200/70 -mb-1">
              💡 Deja ambos importes en <b>0</b> si solo quieres registrar el montaje (sin generar componentes — más rápido).
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-fuchsia-200">Importe Montaje (€)</Label>
              <Input type="number" step="0.01" min="0" value={form.importe_montaje}
                onChange={(e) => setField("importe_montaje", e.target.value)}
                placeholder="50.00"
                className="bg-zinc-900 border-zinc-800" data-testid="rapida-imp-montaje" />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-fuchsia-200">Importe Producción (€)</Label>
              <Input type="number" step="0.01" min="0" value={form.importe_produccion}
                onChange={(e) => setField("importe_produccion", e.target.value)}
                placeholder="0.00"
                className="bg-zinc-900 border-zinc-800" data-testid="rapida-imp-produccion" />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-fuchsia-200">Quién produce</Label>
              <Input value={form.quien_produce}
                onChange={(e) => setField("quien_produce", e.target.value)}
                placeholder="DNF o nombre proveedor"
                className="bg-zinc-900 border-zinc-800" data-testid="rapida-quien" />
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-fuchsia-200">Pedido (ref. cliente)</Label>
              <Input value={form.referencia_cliente}
                onChange={(e) => setField("referencia_cliente", e.target.value)}
                placeholder="PL252526"
                className="bg-zinc-900 border-zinc-800" data-testid="rapida-pedido" />
            </div>
          </div>

          {/* === Fila 6: Observaciones === */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Observaciones</Label>
            <Textarea value={form.observaciones}
              onChange={(e) => setField("observaciones", e.target.value)}
              rows={2}
              placeholder="Notas internas (PCrepair RECOGER, horarios…)"
              className="bg-zinc-900 border-zinc-800 resize-none"
              data-testid="rapida-observaciones" />
          </div>
          {/* === Fila 7: Adjuntos con Drag & Drop === */}
          <div
            onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              const files = Array.from(e.dataTransfer.files || []);
              if (files.length) setDroppedFiles((prev) => [...prev, ...files]);
            }}
            className={`rounded border-2 border-dashed p-4 transition-colors ${dragActive
              ? "border-amber-400 bg-amber-500/10"
              : "border-zinc-700 bg-zinc-950/40"}`}
            data-testid="rapida-dropzone">
            <div className="flex items-center gap-3">
              <Upload className={`w-5 h-5 ${dragActive ? "text-amber-300" : "text-zinc-500"}`} />
              <div className="flex-1">
                <div className="text-sm text-zinc-200 font-medium">
                  Arrastra archivos aquí (PDF, imágenes, AI, ZIP…)
                </div>
                <div className="text-xs text-zinc-500">
                  O <label className="text-amber-300 underline cursor-pointer">selecciónalos
                    <input type="file" multiple hidden onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length) setDroppedFiles((prev) => [...prev, ...files]);
                      e.target.value = "";
                    }} data-testid="rapida-file-input" />
                  </label>. Máx. 20 MB cada uno.
                </div>
              </div>
              {droppedFiles.length > 0 && (
                <span className="text-xs bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 px-2 py-1 rounded">
                  {droppedFiles.length} archivo(s)
                </span>
              )}
            </div>
            {droppedFiles.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {droppedFiles.map((f, i) => (
                  <div key={i} className="inline-flex items-center gap-2 bg-zinc-800/80 border border-zinc-700 rounded px-2 py-1 text-xs">
                    <FileIcon className="w-3 h-3 text-zinc-400" />
                    <span className="text-zinc-200 max-w-[180px] truncate">{f.name}</span>
                    <span className="text-zinc-500">{(f.size / 1024).toFixed(0)}KB</span>
                    <button type="button"
                      onClick={() => setDroppedFiles((p) => p.filter((_, idx) => idx !== i))}
                      className="text-zinc-500 hover:text-red-300"
                      data-testid={`rapida-file-remove-${i}`}>
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving || uploadingFiles}>Cancelar</Button>
          <Button
            variant="outline"
            onClick={() => submit(false)}
            disabled={saving || !form.cliente_id}
            data-testid="btn-rapida-guardar-otro"
            className="bg-zinc-900 border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar y otra"}
          </Button>
          <Button
            onClick={() => submit(true)}
            disabled={saving || !form.cliente_id}
            data-testid="btn-rapida-guardar"
            className="bg-fuchsia-500 hover:bg-fuchsia-400 text-zinc-950 font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Crear petición
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
